import { SOPVersion, Step } from "../types/sop";
import { api } from "./client";

export interface SOPResponse {
  sopId: string;
  sopTitle: string;
  versions: SOPVersion[];
  createdAt: string;
}

export interface RegenerateSopTitleResponse {
  version_title: string;
  message?: string;
}

export interface RegenerateSopStepsResponse {
  candidates: {
    /** The SOPRegenerationCandidate record ID — used to attach feedback to this specific candidate. */
    id: string;
    stepIndex: number;
    candidate: Step;
    /**
     * True when the owner denied this candidate and it is queued for admin to regenerate
     * with the attached feedback. The UI shows a "reprocessing" state instead of accept/deny.
     */
    needsRegen?: boolean;
  }[];
}

export interface RegenerateSopLocksResponse {
  lockedStepIndices: number[];
  ownerCanReview: boolean;
  ownerVisibleAny: boolean;
}

/** Allowed mediaType values for regenerate_sop_steps mediaRefs. */
export type RegenerateFileType =
  | "video"
  | "image"
  | "step_file"
  | "assembly"
  | "bom";

export interface RegenerateMediaRef {
  mediaId: string;
  mediaType: RegenerateFileType;
}

/** Payload for regenerate_sop_steps: any combination of steps, feedback, and files. */
export interface RegenerateSopStepsRequest {
  stepIndices?: number[];
  feedbackIds?: string[];
  mediaRefs?: RegenerateMediaRef[];
  transcriptionLanguageCode?: string;
}

export const sopService = {
  /**
   * Regenerate SOP title using AI
   */
  regenerateTitle: async (sopId: string): Promise<RegenerateSopTitleResponse> => {
    const response = await api.post(`/regenerate_sop_title/${sopId}`);
    return response.data;
  },

  /**
   * Regenerate one or more SOP steps using AI.
   * Sends multipart/form-data when mediaRefs are present, JSON otherwise.
   */
  regenerateSteps: async (
    sopId: string,
    versionId: string,
    payload: RegenerateSopStepsRequest,
  ): Promise<RegenerateSopStepsResponse> => {
    const hasMediaRefs = Boolean(payload.mediaRefs?.length);
    const response = hasMediaRefs
      ? await api.post(
          `/regenerate_sop_steps/${sopId}/${versionId}`,
          (() => {
            const formData = new FormData();
            // Always append these keys (same as legacy multipart): backend may rely on
            // field presence; use [] when omitted. Do not use truthy checks — [] is fine.
            formData.append(
              "stepIndices",
              JSON.stringify(payload.stepIndices ?? []),
            );
            formData.append(
              "feedbackIds",
              JSON.stringify(payload.feedbackIds ?? []),
            );
            formData.append(
              "mediaRefs",
              JSON.stringify(payload.mediaRefs ?? []),
            );
            if (payload.transcriptionLanguageCode) {
              formData.append(
                "transcriptionLanguageCode",
                payload.transcriptionLanguageCode,
              );
            }
            return formData;
          })(),
          { headers: { "Content-Type": "multipart/form-data" } },
        )
      : await api.post(`/regenerate_sop_steps/${sopId}/${versionId}`, payload);
    return response.data;
  },

  /** @deprecated Use regenerateSteps instead. */
  regenerateStepsWithRefIds: async (
    sopId: string,
    versionId: string,
    payload: RegenerateSopStepsRequest,
  ): Promise<RegenerateSopStepsResponse> => {
    return sopService.regenerateSteps(sopId, versionId, payload);
  },

  /**
   * Fetch existing regeneration candidates for an SOP version (e.g. after page refresh).
   * Returns the same shape as regenerateSteps response.
   * Normalizes stepIndex to number (handles backend snake_case or string).
   */
  getRegenerationCandidates: async (
    sopId: string,
    versionId: string,
  ): Promise<RegenerateSopStepsResponse> => {
    const response = await api.get(
      `/regenerate_sop_steps/${sopId}/${versionId}/candidates`,
    );
    const data = response.data as {
      candidates?: Array<{
        id?: string;
        stepIndex?: number | string;
        step_index?: number | string;
        candidate: Step;
        needsRegen?: boolean;
        needs_regen?: boolean;
      }>;
    };
    const raw = data?.candidates ?? [];
    const candidates = raw.map((item) => ({
      id: item.id ?? "",
      stepIndex: Number(item.stepIndex ?? item.step_index ?? -1),
      candidate: item.candidate,
      needsRegen: item.needsRegen ?? item.needs_regen ?? false,
    }));
    return { candidates };
  },

  /**
   * Fetch locked step indices for an SOP version without exposing candidates.
   */
  getRegenerationLocks: async (
    sopId: string,
    versionId: string,
  ): Promise<RegenerateSopLocksResponse> => {
    const response = await api.get(
      `/regenerate_sop_steps/${sopId}/${versionId}/locks`,
    );
    const data = response.data as {
      lockedStepIndices?: Array<number | string>;
      ownerCanReview?: boolean;
      ownerVisibleAny?: boolean;
    };
    return {
      lockedStepIndices: (data.lockedStepIndices ?? []).map((x) => Number(x)),
      ownerCanReview: Boolean(data.ownerCanReview),
      ownerVisibleAny: Boolean(data.ownerVisibleAny),
    };
  },

  /**
   * Update stored regeneration candidates (SuperAdmin only).
   * If notifyOwner is true, sends email to SOP owner after saving.
   */
  updateCandidates: async (
    sopId: string,
    versionId: string,
    candidates: { stepIndex: number; candidate: Step }[],
    notifyOwner?: boolean,
  ): Promise<RegenerateSopStepsResponse> => {
    const response = await api.put(
      `/regenerate_sop_steps/${sopId}/${versionId}/candidates`,
      { candidates, notifyOwner },
    );
    return response.data;
  },

  /**
   * Apply chosen steps (accept/deny merged). Updates version steps, clears all
   * regeneration candidates for this sop/version. Owner or SuperAdmin.
   */
  applyCandidates: async (
    sopId: string,
    versionId: string,
    steps: Step[],
    mediaIdsToDelete?: string[],
    changeRequestCandidateIds?: string[],
  ): Promise<void> => {
    await api.post(
      `/regenerate_sop_steps/${sopId}/${versionId}/apply`,
      {
        steps,
        mediaIdsToDelete: mediaIdsToDelete ?? [],
        // Backend reads this key — kept stable to avoid a backend migration
        deniedCandidateIds: changeRequestCandidateIds ?? [],
      },
    );
  },

  /**
   * Discard all regeneration candidates for this sop/version. Does not change
   * version steps. Owner or SuperAdmin. Use to cancel review and allow step editing again.
   * Pass mediaIds to also clean up the uploaded reference files.
   */
  discardCandidates: async (
    sopId: string,
    versionId: string,
    mediaIds?: string[],
  ): Promise<void> => {
    await api.delete(
      `/regenerate_sop_steps/${sopId}/${versionId}/candidates`,
      { data: { mediaIds: mediaIds ?? [] } },
    );
  },

  migrateSopToOrganization: async (
    sopId: string,
    targetOrgId: string,
  ): Promise<void> => {
    await api.put(`/sops/${sopId}/organization`, {
      organization_id: targetOrgId,
    });
  },
  // getSOPs: async (): Promise<SOPResponse[]> => { ... },
  // getSOP: async (sopId: string): Promise<SOPResponse> => { ... },
  // createSOP: async (data: CreateSOPRequest): Promise<SOPResponse> => { ... },
  // updateSOP: async (sopId: string, data: Partial<SOPResponse>): Promise<SOPResponse> => { ... },
  // deleteSOP: async (sopId: string): Promise<void> => { ... },
};
